import { useTranslation } from 'react-i18next';
import Icon from '@cx-web/components/primitives/icon';
import { useBetslipContext } from '@cx-web/feature-betslip/context';

import { type CategoryGroup, categoryGroups } from '@cx-modules/constants';

import type IBet from '../interfaces/IBet';
import styles from '../style/bet.module.css';

// One bet row inside the betslip. Renders as a <li> whose accessible name is
// `<bet> — <subevent> — <market>`; the remove button reuses that name. Full
// ARIA navigation contract for tests/AT lives at the top of ../betslip.tsx.
const Bet = ({
  bet,
  disabled,
  posInSet,
  setSize,
}: {
  bet: IBet;
  disabled: boolean;
  posInSet?: number;
  setSize?: number;
}): React.JSX.Element => {
  const { getBetMeta, removeBetFromBetslip } = useBetslipContext();
  const { betId, betName, marketName, spread, subeventName } = getBetMeta(bet);

  const { categoryGroupName } = bet;

  const { translationFile } = (categoryGroups.find(cg => cg.name === categoryGroupName) as CategoryGroup) || '';

  const ns = [translationFile, 'label', 'bettype'];
  const { t } = useTranslation(ns);

  function extractTeamsFromSubeventName(subeventName: string) {
    const teamSeparators = [' v ', ' at '];

    for (const separator of teamSeparators) {
      const teams = subeventName.split(separator);
      if (teams.length === 2) {
        const [first, second] = teams.map(team => team.trim());
        return separator === ' v '
          ? { away: second, first, home: first, second, separator, success: true }
          : { away: first, first, home: second, second, separator, success: true };
      }
    }
    return {
      away: '',
      first: '',
      home: '',
      second: '',
      separator: '',
      success: false,
    };
  }

  const translatedRegionalisedSubeventName = () => {
    if (!subeventName) return '';

    const { first, second, separator, success } = extractTeamsFromSubeventName(subeventName);

    if (success) return `${t(first)}${t(separator, { ns: 'label' })}${t(second)}`;
    return t(subeventName);
  };

  const BET_NAME = t(betName);
  const MARKET_NAME = t(marketName, { ns: 'bettype' });
  const SUBEVENT = translatedRegionalisedSubeventName();
  const accessibleName = [BET_NAME, SUBEVENT, MARKET_NAME].filter(Boolean).join(' — ');

  return (
    <li
      aria-label={accessibleName}
      aria-posinset={posInSet}
      aria-setsize={setSize}
      className={[styles.betWrapper, disabled ? styles.disabled : ''].join(' ')}
      data-bet-id={betId}
    >
      <div>
        <span className={styles.name}>{BET_NAME}</span>
        {spread && <span className={styles.spreadValue}>{spread}</span>}
        <div className={styles.subeventName}>{SUBEVENT}</div>
        <div className={styles.marketName}>{MARKET_NAME}</div>
      </div>
      <span
        aria-label={`Remove ${accessibleName} from betslip`}
        className={styles.closeBet}
        data-testid="betslip-close"
        onClick={() => removeBetFromBetslip(betId, disabled, bet)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            removeBetFromBetslip(betId, disabled, bet);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <Icon height={32} icon="Close" width={32} />
      </span>
    </li>
  );
};

export default Bet;
